import { ViewCard } from './views/ViewCard';
import { LockedViewCard } from './LockedViewCard';
import { PaceDegradation } from './views/PaceDegradation';
import { TruePace } from './views/TruePace';
import { CarCharacter } from './views/CarCharacter';
import { ImprovementTrajectory } from './views/ImprovementTrajectory';
import { SectorDominance } from './views/SectorDominance';

const VIEW_REGISTRY = {
  PaceDegradation: {
    title: 'Tyre life pace',
    subtitle: 'Fuel-corrected lap-time slope as tyres age, per compound',
    Component: PaceDegradation,
    info: (
      <>
        <strong className="text-f1-text">Slope of lap time as a tyre ages</strong>, fitted per compound by ordinary least squares with all stints of that compound pooled into a single regression, then <strong className="text-f1-text">fuel-corrected</strong> by adding back ~0.04 s/lap (typical fuel-burn benefit) so the slope reflects pure tyre degradation. Lower (flatter) = the car preserves the tyre better. Strategy engineers use this slope to predict who'll be fastest on lap 30 and to time pit windows.
        <br /><br />
        <span className="text-f1-muted">Read it: shorter bar = less time lost per lap of tyre life. One bar per compound per driver. The number on the right is the driver's best compound slope. Use the toggle to filter by compound and compare like-for-like.</span>
      </>
    ),
  },
  TruePace: {
    title: 'Clean-air pace',
    subtitle: 'Median pace after stripping pit, traffic, and outliers',
    Component: TruePace,
    info: (
      <>
        <strong className="text-f1-text">Median lap time</strong> after dropping pit-affected, lap-1, and slow-side outlier laps (Q3 + 1.5·IQR). The faint vertical tick behind each candle is the <em>raw</em> median — the gap between raw and filtered is how much traffic / yellows masked the car's real pace.
        <br /><br />
        <span className="text-f1-muted">Read it: leftmost driver = fastest in clean air. A large ghost-to-candle gap = a driver who fought through traffic.</span>
      </>
    ),
  },
  CarCharacter: {
    title: 'Car character',
    subtitle: 'Where each car finds lap time: straights vs corners',
    Component: CarCharacter,
    info: (
      <>
        Each car's <strong className="text-f1-text">telemetry signature</strong>: 95th-percentile top speed (X) vs 95th-percentile lateral g (Y), sized by peak braking g. Crosshair = field median.
        <br /><br />
        <span className="text-f1-muted">Read it: top-right = high-downforce all-rounder. Bottom-right = low-drag straight-line car. Top-left = grippy but slow on straights. Reveals each team's design philosophy at a glance.</span>
      </>
    ),
  },
  ImprovementTrajectory: {
    title: 'Track evolution',
    subtitle: 'When each driver unlocked their lap',
    Component: ImprovementTrajectory,
    info: (
      <>
        <strong className="text-f1-text">Best-lap-so-far</strong> per driver across the session — a stair-step as they improve. Top 5 by final best are highlighted; the rest are ghosted for context.
        <br /><br />
        <span className="text-f1-muted">Read it: lines that drop late = drivers who waited for the rubbered-in track. Flat tails on the right = drivers who couldn't improve at the end.</span>
      </>
    ),
  },
  SectorDominance: {
    title: 'Sector dominance',
    subtitle: 'Who owns each sector, lap by lap',
    Component: SectorDominance,
    info: (
      <>
        For each lap, the driver with the fastest <strong className="text-f1-text">S1, S2, S3</strong>. Lit ticks = that driver owned that sector that lap; row totals on the right = total purple sectors taken.
        <br /><br />
        <span className="text-f1-muted">Read it: a driver with many purples spread across all three colors had the complete car. A driver clustered in one color was strong only in that sector.</span>
      </>
    ),
  },
};

const VIEWS_BY_SESSION = {
  FP1: ['PaceDegradation', 'TruePace', 'CarCharacter'],
  FP2: ['PaceDegradation', 'TruePace', 'CarCharacter'],
  FP3: ['PaceDegradation', 'TruePace', 'CarCharacter'],
  Q:   ['ImprovementTrajectory', 'SectorDominance', 'CarCharacter'],
  SQ:  ['ImprovementTrajectory', 'SectorDominance', 'CarCharacter'],
  S:   ['PaceDegradation', 'TruePace', 'CarCharacter'],
  R:   ['PaceDegradation', 'TruePace', 'CarCharacter'],
};

export const AdditionalViewsSection = ({
  race,
  session,
  laps,
  status,
  onSignInClick,
  selectedDrivers,
  hideSlowLaps,
}) => {
  if (!race || !session) return null;
  const viewIds = VIEWS_BY_SESSION[session] || VIEWS_BY_SESSION.R;
  const firstLap = laps && laps[0];
  const hasRichFields = Boolean(
    firstLap &&
      (firstLap.sector1_seconds !== undefined ||
        firstLap.top_speed_kph !== undefined),
  );

  if (!laps || laps.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline">
        <h2 className="text-sm font-semibold tracking-tight text-f1-text">
          More views
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {viewIds.map((id, idx) => {
          const spec = VIEW_REGISTRY[id];
          if (!spec) return null;
          if (!hasRichFields) {
            return (
              <LockedViewCard
                key={id}
                title={spec.title}
                subtitle={spec.subtitle}
                info={spec.info}
                onSignInClick={onSignInClick}
                seed={idx}
              />
            );
          }
          const componentProps =
            id === 'PaceDegradation'
              ? {
                  race: race?.slug,
                  session,
                  selectedDrivers,
                  hideSlowLaps,
                }
              : { laps, status, session };
          return (
            <ViewCard key={id} title={spec.title} subtitle={spec.subtitle} info={spec.info}>
              <spec.Component {...componentProps} />
            </ViewCard>
          );
        })}
      </div>
    </section>
  );
};
